<script setup lang="ts">
import { useActionPointStore } from '@/stores/actionPoint';
import { getTotalCountFromChartData } from '@/utils/chart';
import { currentYear, getMonthNames } from '@/utils/date';
import { processErrorWithFeedback } from '@/utils/error';
import type { ChartData, ChartOptions } from 'chart.js';
import { computed, inject, onMounted, ref, type ComputedRef } from 'vue';
import { Line } from 'vue-chartjs';

const actionPointStore = useActionPointStore();

const props = defineProps<{
    chartOptions?: ChartOptions<'line'>;
    title: string;
    emptyMessage: string;
}>();

const options = { ...props.chartOptions };
const totalCount = computed(() => (chartData.value != null ? getTotalCountFromChartData(chartData.value) : 0));

const selectedYear = ref<number>(currentYear);
const chartData = ref<ChartData<'line', number[], string>>();
const isLoading = ref(true);

const organizationId = inject('organizationId') as ComputedRef<string>;

onMounted(fetchTimelineData);

async function fetchTimelineData() {
    isLoading.value = true;
    const result = await actionPointStore.getTimeLineChartData(organizationId.value, selectedYear.value);

    if (result.status != 200) {
        isLoading.value = false;
        processErrorWithFeedback('Tijdlijn van actiepunten kon niet worden opgehaald.', result);
    }

    chartData.value = {
        labels: getMonthNames(),
        datasets: [
            {
                backgroundColor: '#062558',
                borderColor: '#062558',
                pointBorderColor: '#062558',
                pointBackgroundColor: '#062558',
                data: Object.values(result.object)
            }
        ]
    };

    isLoading.value = false;
}
</script>

<template>
    <div class="rounded-xl shadow-xl pa-0">
        <v-row class="text-center my-0 pa-2 rounded-t-xl bg-main-dark justify-center align-center">
            <h4 class="d-inline-block mx-5 my-0 text-white">{{ title }}</h4>
            <v-select
                v-model="selectedYear"
                :items="[currentYear - 5, currentYear - 4, currentYear - 3, currentYear - 2, currentYear - 1, currentYear]"
                placeholder="Selecteer een jaar"
                @update:modelValue="fetchTimelineData"
                hide-details
                density="compact"
                class="d-inline-block"
                style="width: auto"
            />
        </v-row>

        <div class="rounded-b-xl pa-lg-2 d-flex justify-center align-center" style="height: 17.5em">
            <v-progress-circular v-if="isLoading" color="primary" indeterminate />
            <div v-else>
                <div class="d-flex" style="height: 14em">
                    <Line v-if="chartData && totalCount > 0" :data="chartData" :options="options" class="w-100" />
                    <p v-else class="d-flex align-center">{{ emptyMessage }}</p>
                </div>
                <p class="text-center ma-0">Totaal {{ totalCount }}</p>
            </div>
        </div>
    </div>
</template>
